import React from 'react';
import { CheckIcon, ChevronUpIcon } from 'lucide-react';
import QuestionField from './QuestionField';
import { sectionIcons } from './sectionIcons';

// `question.versionBadge` — set only on a form built by mergeSectionVersions — used to render a
// text pill here, above the field. Being in the grid cell's normal flow, it pushed a badged
// question's label and input below its un-badged neighbour and broke the two-column alignment.
// It is now a VersionHint inside the question's own label, rendered by QuestionField.

// Matches the reference design's SectionCard.tsx 1:1 (step badge, icon circle, chevron,
// two-column field grid), adapted to this app's questionKey-keyed values/errors contract.
const SectionCard = ({ section, index, values, errors, progress, open, onToggle, onChange, context }) => {
    const Icon = sectionIcons[section.icon];
    const questions = section.questions.filter(question => question.enabled);
    const panelId = `panel-${section.sectionKey}`;

    return (
        <section id={section.sectionKey} aria-labelledby={`heading-${section.sectionKey}`} className="tw-scroll-mt-32 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-sand-200 tw-bg-white tw-shadow-sm">
            <h2 id={`heading-${section.sectionKey}`}>
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={open}
                    aria-controls={panelId}
                    className="tw-flex tw-w-full tw-items-center tw-gap-4 tw-px-5 tw-py-4 tw-text-left tw-transition hover:tw-bg-sand-50">
                    <span className={`tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl ${progress.isComplete ? 'tw-bg-brand-600 tw-text-white' : 'tw-bg-brand-50 tw-text-brand-700'}`}>
                        {progress.isComplete ? <CheckIcon className="tw-h-5 tw-w-5" aria-hidden="true" /> : (Icon ? <Icon className="tw-h-5 tw-w-5" aria-hidden="true" /> : null)}
                    </span>
                    <span className="tw-min-w-0 tw-flex-1">
                        <span className="tw-flex tw-items-center tw-gap-2">
                            <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-brand-500">
                                Step {String(index + 1).padStart(2, '0')}
                            </span>
                            {progress.required > 0 ? (
                                <span className="tw-text-[11px] tw-font-semibold tw-text-gray-400">
                                    {progress.completed}/{progress.required} required complete
                                </span>
                            ) : null}
                        </span>
                        <span className="tw-block tw-truncate tw-text-base tw-font-semibold tw-text-gray-900">{section.title}</span>
                        {section.description ? <span className="tw-mt-0.5 tw-block tw-truncate tw-text-xs tw-text-gray-500">{section.description}</span> : null}
                    </span>
                    <ChevronUpIcon className={`tw-h-5 tw-w-5 tw-shrink-0 tw-text-gray-400 tw-transition-transform ${open ? '' : 'tw-rotate-180'}`} aria-hidden="true" />
                </button>
            </h2>
            {open ? (
                <div id={panelId} className="tw-border-t tw-border-sand-200 tw-px-5 tw-py-6">
                    <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-5 sm:tw-grid-cols-2">
                        {questions.map(question => (
                            <div key={question.questionKey} className={question.width === 'half' && question.type !== 'info' ? 'sm:tw-col-span-1' : 'sm:tw-col-span-2'}>
                                <QuestionField
                                    question={question}
                                    value={values[question.questionKey]}
                                    error={errors?.[question.questionKey]}
                                    onChange={value => onChange(question.questionKey, value)}
                                    context={context} />
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </section>
    );
};

export default SectionCard;
